import { LazyLoader, showToast, useDefaultSelecting } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'urql';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { backendApi } from '../../utils/backendApi';
import { showErrorToast } from '../../utils/showErrorToast';
import { showSuccessToast } from '../../utils/showSuccessToast';
import { BpCard } from '../BpCard/BpCard';
import { useProcuratGroups } from './hooks/useProcuratGroups';
import { ProcuratGroupsTable } from './tables/ProcuratGroupsTable';

const ProcuratGroupsQueryDocument = graphql(`
  query ProcuratGroupsData($organizationUuid: ID!) {
    groups(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      foreignRefId
      updatedAt
      createdAt
    }
  }
`);

export const ProcuratGroups = () => {
  const { t } = useTranslation();
  const { organization } = useContext(OrganizationConfigContext);

  const { groups, isFetching, refetchGroups } = useProcuratGroups();
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

  const [{ data }, reloadBpGroups] = useQuery({
    query: ProcuratGroupsQueryDocument,
    variables: {
      organizationUuid: organization?.uuid ?? '',
    },
  });

  const tableData = useMemo(() => {
    return (
      groups?.personGroups?.map((group) => {
        const bpGroup = data?.groups.find((c) => !!c.foreignRefId?.includes(group.id.toString()));
        return {
          ...group,
          modificationDate: bpGroup?.updatedAt ? bpGroup?.updatedAt : bpGroup?.createdAt ? bpGroup?.createdAt : null,
        };
      }) ?? []
    );
  }, [groups, data?.groups]);

  const [importing, setImporting] = useState(false);

  const handleImport = async () => {
    showToast(t('procuratImport.importStarted'), {
      type: 'info',
      theme: 'colored',
    });

    setImporting(true);

    try {
      const selectedGroups = tableData.filter((_, index) => Object.keys(rowSelection).includes(index.toString()));
      const importData = selectedGroups.map((group) => ({ id: group.id }));

      const resp = await backendApi.put('/procurat/groups/import', JSON.stringify(importData));

      if (resp.ok) {
        reloadBpGroups({ requestPolicy: 'network-only' });
        await refetchGroups();
        showSuccessToast(t('procuratImport.importSuccess'));
        onRowSelectionChange({});
      }

      if (!resp.ok) {
        showErrorToast({
          graphQLErrors: [],
          name: '',
          message: resp.statusText,
        });
      }
    } catch (error) {
      showErrorToast({
        graphQLErrors: [],
        name: '',
        message: error instanceof Error ? error.message : 'Import failed',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <BpCard header={{ headline: t('groups.title') }} noPadding>
      {importing || isFetching ? (
        <LazyLoader embedded forceHeight='15vh' />
      ) : (
        <ProcuratGroupsTable
          data={tableData}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          onImport={handleImport}
        />
      )}
    </BpCard>
  );
};
